import { memo, useState } from "react";
import { useAuth } from "../../utils/hooks/useAuth";
import Icon from "../../components/Icon/Icon";
import styles from "./styles.module.scss";
import {
  jumboMailLogo,
  giftModalImage1,
  giftModalImage2,
} from "../../assets/images";
import { P, Span } from "../../components/Text/Text";
import { useTranslate } from "../../utils/hooks/useTranslate";
import { useLazyGetEncryptedEmailForCurrentUserQuery } from "../../api/encryptedEmailsAPI";
import usePopup from "../../utils/hooks/usePopup";
import { useAppSelector } from "../../store/hooks";
import { selectDirection } from "../../store/selectors/appSelectors";
import { DirectionE } from "../../models/enums";
import { clss } from "../../utils/helpers/styleHelpers";

/**
 * JUMBOmail Premium modal component.
 * Shows promotional offer for one month free trial of JUMBOmail Premium.
 */
const GiftCampaignModal = memo(() => {
  const { currentUser } = useAuth();
  const { removePopup } = usePopup();
  const { t } = useTranslate();
  const direction = useAppSelector(selectDirection);
  const [getEncryptedEmail] = useLazyGetEncryptedEmailForCurrentUserQuery();
  const [isLoading, setIsLoading] = useState(false);

  const userName = currentUser?.userName;
  const userEmail = currentUser?.email;

  const handleRedeem = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      const data = await getEncryptedEmail().unwrap();

      const encrypted = (data && data.encryptedEmail) ?? null;

      if (!encrypted) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch encrypted email: empty response", data);
        setIsLoading(false);
        return;
      }

      const url = `https://www.jumbomail.me/he/campaign/jumbomedia/${encodeURIComponent(
        encrypted
      )}`;
      window.open(url, "_blank", "noopener,noreferrer");
      removePopup();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch encrypted email:", error);
      setIsLoading(false);
    }
  };

  const features = [
    "Send files up to 1TB without",
    "full branding every shipment looks like",
    "advanced control download tracking password protection",
    "branded form for receiving large files",
    "central management of all company users",
  ];

  return (
    <div className={styles.modalContent}>
      <div className={styles.header}>
        {/* Background floating file images */}
        <img
          src={giftModalImage2}
          alt=""
          aria-hidden="true"
          className={styles.filesBackgroundLeft}
        />
        <img
          src={giftModalImage1}
          alt=""
          aria-hidden="true"
          className={styles.filesBackgroundRight}
        />

        <div className={styles.logoSection}>
          <img
            src={jumboMailLogo}
            alt="JUMBOmail"
            className={styles.jumboMailLogo}
          />
          <Icon name="giftBlue" className={styles.giftIcon} />
        </div>
        <h1
          className={styles.headline}
          dangerouslySetInnerHTML={{
            __html: userName
              ? `${userName}${t("You received a special gift from")}`
              : t("You received a special gift from"),
          }}
        />
        <P className={styles.subtitle}>One month trial of jumbomail premium</P>
      </div>

      <div className={clss([styles.body, direction === DirectionE.RTL ? styles.bodyRtl : undefined])}>
        <div className={styles.scrollableContent}>
          <div className={styles.introText}>
            <P>you already know the power of jumbomedia</P>
            <P>But what about all the other files</P>
            <P>With jumbomail premium every file sent gets</P>
          </div>

          <ul className={styles.featuresList}>
            {features.map((feature, index) => (
              <li key={index} className={styles.featureItem}>
                <div className={styles.approvedIcon}>
                  <Icon name="check" />
                </div>
                <Span className={styles.featureText}>{feature}</Span>
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.rightColumn}>
          <div className={styles.actionSection}>
            <P className={styles.premiumStatement}>
              premium service without sacrificing privacy
            </P>

            <P className={styles.redemptionText}>
              {t`To receive the benefit, you must complete the process on the JUMBOmail website with your email address ${userEmail}`}
            </P>

            <button
              className={`${styles.redeemButton} ${
                isLoading ? styles.redeemButtonLoading : ""
              }`}
              onClick={handleRedeem}
              disabled={isLoading}
              id="button-gift-redeem"
              name="button-gift-redeem"
            >
              <Span>To redeem the gift</Span>
              {!isLoading && (
                <Icon
                  name="arrow"
                  className={clss([
                    styles.arrowIcon,
                    direction === DirectionE.LTR ? styles.arrowIconLtr : undefined,
                  ])}
                />
              )}
            </button>
            <Span className={styles.noObligationText}>
              no obligation no credit card
            </Span>
          </div>

          <div className={styles.footer}>
            <ul className={styles.trialInfo}>
              <li>
                <Icon name="security" />
                <Span>secure and protected</Span>
              </li>
              <li>
                <Icon name="clockGreen" />
                <Span>30day trial</Span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
});

export default GiftCampaignModal;